/**
 * Project intake (2026-09-28): propose a project, score it, rank the
 * backlog, decide, convert. Backend: app/services/intake.py.
 *
 * Everyone can submit and see the queue; scoring, decisions, conversion and
 * weights are for workspace admins (the server enforces this too).
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { CheckCircle2, Inbox, Plus, XCircle, Clock, ArrowRightCircle } from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { useAuth } from '../contexts/AuthContext'
import { intakeService, CRITERION_LABEL, type IntakeRequest, type IntakeList } from '../services/intake'
import { apiErrorMessage } from '../utils/apiError'

const STATUS_LABEL: Record<string, string> = {
  submitted: 'New', under_review: 'Scored', approved: 'Approved', rejected: 'Rejected',
  deferred: 'Deferred', converted: 'Now a project',
}

const empty = { title: '', description: '', business_case: '', sponsor: '', estimated_cost: '', estimated_weeks: '', target_start: '' }

export default function IntakePage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = Boolean(user?.is_superuser || user?.roles?.some((r) => ['Organization Owner', 'admin', 'Admin'].includes(r.name)))
  const [data, setData] = useState<IntakeList | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(empty)
  const [busy, setBusy] = useState(false)
  const [openId, setOpenId] = useState<number | null>(null)

  const load = useCallback(async () => {
    try {
      setData(await intakeService.list())
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not load requests.'))
    }
  }, [])

  useEffect(() => { load() }, [load])

  const submit = async () => {
    if (form.title.trim().length < 2) {
      toast.error('Give the request a title.')
      return
    }
    setBusy(true)
    try {
      await intakeService.submit({
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        business_case: form.business_case.trim() || undefined,
        sponsor: form.sponsor.trim() || undefined,
        estimated_cost: form.estimated_cost ? Number(form.estimated_cost) : undefined,
        estimated_weeks: form.estimated_weeks ? Number(form.estimated_weeks) : undefined,
        target_start: form.target_start || undefined,
      })
      toast.success('Request submitted.')
      setForm(empty)
      setShowForm(false)
      await load()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not submit the request.'))
    } finally {
      setBusy(false)
    }
  }

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true)
    try {
      await fn()
      toast.success(ok)
      await load()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'That didn’t work. Try again.'))
    } finally {
      setBusy(false)
    }
  }

  const input = 'min-h-[44px] w-full rounded-md border border-border bg-background px-3 text-sm'

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-foreground"><Inbox className="h-6 w-6" /> Project requests</h1>
          <p className="text-sm text-muted-foreground">
            Propose new work here. Requests are scored on value, strategic fit, urgency, risk and effort, and ranked so the best ideas get funded first.
          </p>
        </div>
        <Button onClick={() => setShowForm((v) => !v)} className="min-h-[44px] gap-2"><Plus className="h-4 w-4" /> New request</Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader><CardTitle>New project request</CardTitle></CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <label className="sm:col-span-2 text-sm">Title *
              <input className={input} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </label>
            <label className="sm:col-span-2 text-sm">What is it?
              <textarea className={`${input} min-h-[80px] py-2`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </label>
            <label className="sm:col-span-2 text-sm">Business case: why now, and what do we gain?
              <textarea className={`${input} min-h-[80px] py-2`} value={form.business_case} onChange={(e) => setForm({ ...form, business_case: e.target.value })} />
            </label>
            <label className="text-sm">Sponsor
              <input className={input} value={form.sponsor} onChange={(e) => setForm({ ...form, sponsor: e.target.value })} />
            </label>
            <label className="text-sm">Estimated cost
              <input className={input} type="number" min={0} value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} />
            </label>
            <label className="text-sm">Estimated duration (weeks)
              <input className={input} type="number" min={1} value={form.estimated_weeks} onChange={(e) => setForm({ ...form, estimated_weeks: e.target.value })} />
            </label>
            <label className="text-sm">Earliest start
              <input className={input} type="date" value={form.target_start} onChange={(e) => setForm({ ...form, target_start: e.target.value })} />
            </label>
            <div className="sm:col-span-2 flex gap-2">
              <Button onClick={submit} disabled={busy} className="min-h-[44px]">Submit request</Button>
              <Button variant="outline" onClick={() => setShowForm(false)} className="min-h-[44px]">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!data ? (
        <p className="text-sm text-muted-foreground">Loading requests…</p>
      ) : data.requests.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">No requests yet. Submit the first one.</CardContent></Card>
      ) : (
        <ul className="space-y-3" aria-label="Project requests, best first">
          {data.requests.map((r) => (
            <RequestRow key={r.id} r={r} data={data} isAdmin={isAdmin} busy={busy} open={openId === r.id}
              onToggle={() => setOpenId(openId === r.id ? null : r.id)}
              onScore={(scores) => act(() => intakeService.score(r.id, scores), 'Scores saved.')}
              onDecide={(decision, note) => act(() => intakeService.decide(r.id, decision, note), `Request ${decision}.`)}
              onConvert={() => act(async () => {
                const res = await intakeService.convert(r.id)
                navigate(`/project/${res.project_id}`)
              }, 'Project created from the request.')} />
          ))}
        </ul>
      )}
    </div>
  )
}

function RequestRow({ r, data, isAdmin, busy, open, onToggle, onScore, onDecide, onConvert }: {
  r: IntakeRequest; data: IntakeList; isAdmin: boolean; busy: boolean; open: boolean
  onToggle: () => void
  onScore: (s: Record<string, number>) => void
  onDecide: (d: 'approved' | 'rejected' | 'deferred', note?: string) => void
  onConvert: () => void
}) {
  const [scores, setScores] = useState<Record<string, number>>(r.scores)
  const [note, setNote] = useState('')
  const decidable = r.status === 'submitted' || r.status === 'under_review' || r.status === 'deferred'

  return (
    <li className="rounded-xl border border-border bg-card">
      <button type="button" onClick={onToggle} aria-expanded={open}
        className="flex w-full flex-wrap items-center justify-between gap-3 p-4 text-left min-h-[44px]">
        <span className="flex items-center gap-3">
          {r.rank != null && <span className="w-8 text-center text-lg font-semibold text-muted-foreground">#{r.rank}</span>}
          <span>
            <span className="block font-medium text-foreground">{r.title}</span>
            <span className="text-xs text-muted-foreground">
              {STATUS_LABEL[r.status] ?? r.status}
              {r.estimated_cost != null ? ` · cost ${r.estimated_cost.toLocaleString()}` : ''}
              {r.estimated_weeks ? ` · ${r.estimated_weeks} weeks` : ''}
            </span>
          </span>
        </span>
        <span className="text-right">
          <span className="block text-xl font-semibold text-foreground">{r.score != null ? Math.round(r.score) : '–'}</span>
          <span className="text-xs text-muted-foreground">score / 100</span>
        </span>
      </button>

      {open && (
        <div className="space-y-4 border-t border-border p-4 text-sm">
          {r.description && <p className="text-foreground">{r.description}</p>}
          {r.business_case && <p className="text-muted-foreground"><strong className="text-foreground">Business case:</strong> {r.business_case}</p>}
          {r.decision_note && <p className="text-muted-foreground"><strong className="text-foreground">Decision note:</strong> {r.decision_note}</p>}

          {isAdmin && decidable && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Score (1 = low, 5 = high)</CardTitle>
                <CardDescription>For risk and effort, lower is better.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-5">
                {data.criteria.map((c) => (
                  <label key={c} className="text-xs">
                    {CRITERION_LABEL[c] ?? c} ({Math.round((data.weights[c] ?? 0) * 100)}%)
                    <select value={scores[c] ?? ''} aria-label={CRITERION_LABEL[c] ?? c}
                      onChange={(e) => setScores({ ...scores, [c]: Number(e.target.value) })}
                      className="mt-1 min-h-[44px] w-full rounded-md border border-border bg-background px-2">
                      <option value="" disabled>–</option>
                      {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </label>
                ))}
                <div className="sm:col-span-5">
                  <Button variant="outline" disabled={busy} onClick={() => onScore(scores)} className="min-h-[44px]">Save scores</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {isAdmin && decidable && (
            <div className="flex flex-wrap items-end gap-2">
              <label className="min-w-[220px] flex-1 text-xs">Decision note (optional)
                <input value={note} onChange={(e) => setNote(e.target.value)}
                  className="mt-1 min-h-[44px] w-full rounded-md border border-border bg-background px-3" />
              </label>
              <Button disabled={busy} onClick={() => onDecide('approved', note)} className="min-h-[44px] gap-1"><CheckCircle2 className="h-4 w-4" /> Approve</Button>
              <Button variant="outline" disabled={busy} onClick={() => onDecide('deferred', note)} className="min-h-[44px] gap-1"><Clock className="h-4 w-4" /> Defer</Button>
              <Button variant="outline" disabled={busy} onClick={() => onDecide('rejected', note)} className="min-h-[44px] gap-1"><XCircle className="h-4 w-4" /> Reject</Button>
            </div>
          )}

          {isAdmin && r.status === 'approved' && (
            <Button disabled={busy} onClick={onConvert} className="min-h-[44px] gap-2"><ArrowRightCircle className="h-4 w-4" /> Turn into a project</Button>
          )}
          {r.status === 'converted' && r.project_id && (
            <a href={`/project/${r.project_id}`} className="text-sm underline">Open the project</a>
          )}
        </div>
      )}
    </li>
  )
}
